import { Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map, startWith } from 'rxjs/operators';

/**
 * Walks the activated route tree depth-first and returns the value of `key`
 * from the deepest child route's snapshot data, or `undefined` if not set.
 *
 * Usage: set `data: { myKey: true }` on a route and call
 * `resolveRouteData<boolean>(router, 'myKey')` to read it from any component.
 */
export function resolveRouteData<T>(router: Router, key: string): T | undefined {
  let route = router.routerState.root;
  while (route.firstChild) {
    route = route.firstChild;
  }
  return route.snapshot.data[key] as T | undefined;
}

/**
 * Returns a Signal that reflects the value of `key` from the deepest activated
 * route's data, updated reactively on every navigation.
 *
 * Must be called within an injection context (e.g. a class field initializer or constructor).
 *
 * Usage: set `data: { myKey: true }` on a route and declare
 * `readonly myKey = routeDataSignal<boolean>(router, 'myKey')` in your component.
 */
export function routeDataSignal<T>(router: Router, key: string): Signal<T | undefined> {
  return toSignal(
    router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      map(() => resolveRouteData<T>(router, key)),
      startWith(resolveRouteData<T>(router, key)),
    ),
    { initialValue: resolveRouteData<T>(router, key) },
  );
}
